/**
 * Housing and vehicles - where money goes once you have some.
 *
 * Research finding: "I am rich but bored" is the genre's most common late-game
 * complaint, and its root cause is that money has nowhere to go. Even at V1
 * scale, cash must convert into things the player can see: a bike parked
 * outside, a truck, a better room. Both of these are also mechanically real -
 * the vehicle cuts travel time, the studio improves sleep.
 */
import type { GameState } from '../state/GameState.js';
import type { EventBus } from '../core/events.js';
import type { FinanceSystem } from './FinanceSystem.js';
import { HOUSING, VEHICLES, getHousing, VEHICLES_BY_ID } from '../data/catalog.js';
import { BALANCE } from '../data/balance.js';

export class PropertySystem {
  constructor(private readonly bus: EventBus) {}

  availableHousing(state: GameState) {
    return HOUSING.filter((h) => h.id !== state.player.housingId);
  }

  moveTo(state: GameState, housingId: string, finance: FinanceSystem): boolean {
    const target = HOUSING.find((h) => h.id === housingId);
    if (!target || target.id === state.player.housingId) return false;
    const current = getHousing(state.player.housingId);
    // The old deposit comes back, the new one goes out.
    const net = target.deposit - current.deposit;
    if (net > 0) {
      if (!finance.debit(state, net, 'housing', `Deposit - ${target.name}`)) return false;
    } else if (net < 0) {
      finance.credit(state, -net, 'housing', `Deposit refund - ${current.name}`);
    }
    state.player.housingId = target.id;
    this.bus.emit({ type: 'housing.upgraded', name: target.name });
    return true;
  }

  availableVehicles(state: GameState) {
    return VEHICLES.filter((v) => v.id !== state.player.vehicleId);
  }

  buyVehicle(state: GameState, vehicleId: string, finance: FinanceSystem): boolean {
    const target = VEHICLES.find((v) => v.id === vehicleId);
    if (!target || target.id === state.player.vehicleId) return false;

    let price = target.price;
    // Trading up refunds the resale value of what you already have.
    const owned = state.player.vehicleId ? VEHICLES_BY_ID.get(state.player.vehicleId) : null;
    if (owned) price -= Math.round(owned.price * owned.resale);

    if (price > 0) {
      if (!finance.debit(state, price, 'vehicle', target.name)) return false;
    } else if (price < 0) {
      finance.credit(state, -price, 'sale', `Trade-in - ${owned?.name ?? ''}`);
    }
    const first = state.player.vehicleId === null;
    state.player.vehicleId = target.id;
    // A newly bought vehicle arrives with a full tank and no recorded parking
    // spot; the client puts it on the driveway when it finds none. Buying a
    // car and being handed an empty one would be a joke that stops being funny
    // on the walk to the pump.
    state.player.fuel = BALANCE.vehicle.fuelCapacity;
    if (first) state.player.vehicleParked = null;
    this.bus.emit({ type: 'vehicle.purchased', name: target.name });
    return true;
  }

  /** Remember where the player left the car, so a save restores it there. */
  setVehicleParked(state: GameState, x: number, z: number, heading: number): void {
    if (!state.player.vehicleId) return;
    state.player.vehicleParked = { x, z, heading };
  }

  /**
   * Burn fuel for a distance driven, in metres.
   *
   * Returns the litres actually consumed, which is less than requested when
   * the tank runs dry. The client stops the engine on empty rather than
   * letting the car coast on nothing.
   */
  burnFuel(state: GameState, metres: number): number {
    if (!state.player.vehicleId || metres <= 0) return 0;
    const wanted = (metres / 1000) * BALANCE.vehicle.litresPerKm;
    const burnt = Math.min(state.player.fuel, wanted);
    state.player.fuel = Math.max(0, state.player.fuel - burnt);
    return burnt;
  }

  /**
   * Fill the tank, paying for what actually goes in.
   *
   * Charging for a full tank regardless of how empty it was would make topping
   * up strictly worse than running dry, which is exactly backwards.
   */
  refuel(state: GameState, finance: FinanceSystem): boolean {
    if (!state.player.vehicleId) return false;
    const litres = BALANCE.vehicle.fuelCapacity - state.player.fuel;
    if (litres < 0.5) return false;
    const cost = Math.round(litres * BALANCE.vehicle.fuelPricePerLitre);
    if (!finance.debit(state, cost, 'transport', 'Fuel')) return false;
    state.player.fuel = BALANCE.vehicle.fuelCapacity;
    return true;
  }

  /** Multiplier on travel time. Lower is faster. */
  travelFactor(state: GameState): number {
    if (!state.player.vehicleId) return 1;
    return VEHICLES_BY_ID.get(state.player.vehicleId)?.travelFactor ?? 1;
  }

  /** In-game minutes to cover a distance in metres, on foot or otherwise. */
  travelMinutes(state: GameState, metres: number): number {
    const base = metres / BALANCE.travel.walkSpeed;
    return Math.max(1, Math.round(base * this.travelFactor(state)));
  }
}
